import { z } from "zod";
import { LocationStatus } from "@models/location";
import type { UpdateLocationAddressRequest } from "@models/location";
import type { Location } from "@models/organization";
import {
    certificateFileError,
    CERTIFICATE_REQUIRED_MESSAGE,
} from "@utils/certificateFile";
import {
    addressFormFields,
    canonicalState,
    countryToSend,
} from "./addressForm";

// The Edit Location form (C6, API #433): the address as separate fields, and
// a resale certificate with a change of state.

// The statuses whose address an admin edits here. A rejected location is
// resubmitted by its customer; a suspended or status-less one is not one the
// admin reviews (the API answers status_not_editable).
const EDITABLE_STATUSES: ReadonlySet<LocationStatus | undefined> = new Set([
    LocationStatus.Approved,
    LocationStatus.PendingPayment,
    LocationStatus.Pending,
]);

export const canEditAddress = (status: LocationStatus | undefined): boolean =>
    EDITABLE_STATUSES.has(status);

// The API refuses an empty file (OB-13); the shared rules check type and size.
export const EMPTY_CERTIFICATE_MESSAGE = "The file is empty. Choose another file.";

// The resale certificate an admin uploads (Edit Location's change of state,
// the New Client drawer): required, not empty, and of a type and size the API
// takes. Null when it passes.
export const resaleCertificateError = (file: File | undefined): string | null =>
    !file
        ? CERTIFICATE_REQUIRED_MESSAGE
        : file.size === 0
            ? EMPTY_CERTIFICATE_MESSAGE
            : certificateFileError(file);

// Both sides are canonical (trimmed, upper case, as the API compares them):
// the select emits codes, and the prefill canonicalises the stored state into
// both, so a stored "ne " opens as no change of state. An emptied state
// ("Select a state") is a missing state, which its own rule refuses, not a
// move to another one: it asks for no certificate.
export const isStateChange = (values: {
    state: string;
    loadedState: string;
}): boolean => values.state !== "" && values.state !== values.loadedState;

export const locationFormSchema = z
    .object({
        ...addressFormFields,
        // The state the form was filled with, as the API stores it: a change
        // of state is measured against it.
        loadedState: z.string(),
        // Registered only while the state differs from loadedState.
        resaleCertificate: z.custom<FileList>().optional(),
    })
    .superRefine((values, context) => {
        if (!isStateChange(values)) {
            return;
        }

        const message = resaleCertificateError(values.resaleCertificate?.[0]);

        if (message) {
            context.addIssue({
                code: "custom",
                path: ["resaleCertificate"],
                message,
            });
        }
    });

export type LocationFormValues = z.infer<typeof locationFormSchema>;

// Filled from the client detail's location. Every address field is optional
// on the shared Location type; the admin detail always sends them.
export const toLocationFormValues = (
    location: Location,
): LocationFormValues => ({
    streetAddress: location.streetAddress ?? "",
    city: location.city ?? "",
    state: canonicalState(location.state ?? ""),
    zipCode: location.zipCode ?? "",
    loadedState: canonicalState(location.state ?? ""),
    resaleCertificate: undefined,
});

// `readAs` is the location the form was filled from. Its country goes back as
// read unless blank (countryToSend), and its updatedAt verbatim: the API
// compares it to the stored instant at 100 ns, so it never goes through a JS
// Date. The certificate goes only with a change of state: the API refuses it
// otherwise (certificate_unexpected).
export const toUpdateLocationAddressRequest = (
    values: LocationFormValues,
    readAs: Pick<Location, "country" | "updatedAt">,
): UpdateLocationAddressRequest => ({
    streetAddress: values.streetAddress,
    city: values.city,
    state: values.state,
    zipCode: values.zipCode,
    country: countryToSend(readAs.country ?? ""),
    updatedAt: readAs.updatedAt ?? null,
    resaleCertificate: isStateChange(values)
        ? (values.resaleCertificate?.[0] ?? null)
        : null,
});
